import { defineStore } from "pinia"
import axiosClient from "../axios"
import router from "../router/index.js"
import axios from "axios"

// Constants
const ACCESS_TOKEN_TTL_MS = 22 * 60 * 60 * 1000 // 22 hours

const refreshClient = axios.create({
  baseURL: axiosClient.defaults.baseURL,
  headers: {
    "Content-Type": "application/json",
  },
})
export const useAuthStore = defineStore("auth", {
  state: () => {
    let storedRefreshToken = null
    let storedUser = null
    let storedAccessToken = null

    try {
      storedRefreshToken = localStorage.getItem("REFRESH_TOKEN")
      storedUser = localStorage.getItem("USER")
      storedAccessToken = localStorage.getItem("ACCESS_TOKEN")
    } catch (error) {
      console.error("Failed to read from localStorage:", error)
    }

    return {
      user: {
        token: storedAccessToken || null,
        refreshToken: storedRefreshToken || null,
        data: storedUser ? JSON.parse(storedUser) : null,
      },
    }
  },

  getters: {
    loggedIn: (state) => {
      if (!state.user.token) return false

      const issuedAt = localStorage.getItem("ACCESS_TOKEN_TIME")
      if (!issuedAt) return true

      const age = Date.now() - parseInt(issuedAt, 10)
      return age <= ACCESS_TOKEN_TTL_MS
    },
    isAdmin: (state) => state.user.data?.ur === "admin",
  },

  actions: {
    setUser(user) {
      this.user.data = user

      if (user) {
        try {
          localStorage.setItem("USER", JSON.stringify(user))
        } catch (error) {
          console.error("Failed to save user to localStorage:", error)
        }
      } else {
        localStorage.removeItem("USER")
      }
    },

    setAccessToken(token) {
      this.user.token = token
      if (token) {
        try {
          localStorage.setItem("ACCESS_TOKEN", token)
          localStorage.setItem("ACCESS_TOKEN_TIME", Date.now().toString())
        } catch (error) {
          console.error("Failed to save access token to localStorage:", error)
        }
      } else {
        localStorage.removeItem("ACCESS_TOKEN")
        localStorage.removeItem("ACCESS_TOKEN_TIME")
      }
    },

    setRefreshToken(token) {
      this.user.refreshToken = token
      if (token) {
        try {
          localStorage.setItem("REFRESH_TOKEN", token)
        } catch (error) {
          console.error("Failed to save refresh token to localStorage:", error)
        }
      } else {
        localStorage.removeItem("REFRESH_TOKEN")
      }
    },

    clearAuth() {
      this.user.token = null
      this.user.refreshToken = null
      this.user.data = null

      localStorage.removeItem("REFRESH_TOKEN")
      localStorage.removeItem("USER")
      localStorage.removeItem("ACCESS_TOKEN")
      localStorage.removeItem("ACCESS_TOKEN_TIME")
    },

    forceLogout() {
      this.clearAuth()
      router.replace({ name: "login" })
    },

    async login(data) {
      try {
        const response = await axiosClient.post("/login", data)
        this.setUser(response.data.data.user)
        this.setAccessToken(response.data.data.tokens.access_token)
        this.setRefreshToken(response.data.data.tokens.refresh_token)

        return response.data.data
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    async refreshToken() {
      try {
        const refreshToken = this.user.refreshToken

        if (!refreshToken) {
          throw new Error("Refresh token not found")
        }
        const response = await refreshClient.post("/refresh-token", {
          refresh_token: refreshToken,
        })

        const newAccessToken =
          response.data?.data?.access_token || response.data?.access_token
        if (newAccessToken) {
          this.setAccessToken(newAccessToken)
          return newAccessToken
        } else {
          throw new Error("Access token not found in response")
        }
      } catch (error) {
        console.error("Refresh token failed:", error)

        // For 429 (Too Many Requests) errors, use existing token if available
        if (error.response?.status === 429) {
          const existingToken = this.user.token
          if (existingToken) {
            return existingToken
          }
          throw error
        }

        // For other errors, just throw the error
        // The decision to logout is delegated to the interceptor
        throw error
      }
    },

    async logout() {
      try {
        const refreshToken = this.user.refreshToken

        if (this.user.token && refreshToken) {
          try {
            await axiosClient.post("/logout", {
              refresh_token: refreshToken,
            })
          } catch (error) {
            console.warn("Logout API call failed:", error)
          }
        }

        this.forceLogout()
      } catch (error) {
        console.error("Logout error:", error)
        this.forceLogout()
      }
    },
  },
})

// ===
// Private helpers
// ===
